/*
================================================================================
File: src/components/chat/AIAssistantChat.js
Description: AI Assistant "BG" using Google Gemini with context awareness
             of Seller Leads and typing animations.
================================================================================
*/
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { GoogleGenerativeAI } from "@google/generative-ai"; // Ensure you run: npm install @google/generative-ai
import { animations } from "../../assets/animations";
import Character from "../common/Character";

// Typing Indicator Component
const TypingIndicator = () => (
  <div className="flex space-x-1 h-3 items-center p-1">
    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce"></div>
  </div>
);

const AIAssistantChat = ({ leads = [], user = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm BD, your AI Real Estate Partner. I have access to your current leads. Ask me to analyze a specific address, draft an offer, or review market trends!",
      sender: "ai",
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Initialize Gemini Client
  // Make sure to set REACT_APP_GEMINI_API_KEY in your .env.local file
  const genAI = useMemo(() => {
    if (!process.env.REACT_APP_GEMINI_API_KEY) {
      console.error(
        "Missing REACT_APP_GEMINI_API_KEY. Please add it to your .env.local file."
      );
    }
    return new GoogleGenerativeAI(
      process.env.REACT_APP_GEMINI_API_KEY || ""
    );
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = useCallback(async () => {
    if (inputText.trim() === "" || isLoading) return;

    // 1. Add User Message
    const newUserMessage = {
      id: Date.now(),
      text: inputText,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, newUserMessage]);
    const userQuery = inputText;
    setInputText("");
    setIsLoading(true);

    try {
      // 2. Construct Context (System Prompt + Data)
      // We limit the data fields sent to save tokens and focus the AI
      const simplifiedLeads = leads.map((l) => ({
        address: l.property_address,
        city: l.property_city,
        state: l.property_state,
        zip: l.property_zip,
        price: l.zestimate2,
        repairs: l.property_repair_costs,
        motivation: l.seller_motivation,
        status: l.status,
      }));

      const systemPrompt = `
        You are "BD", a highly experienced US-based Real Estate Investor and Realtor.
        You are concise, professional, and profit-driven.
        You use the Imperial system (sqft, miles).

        CONTEXT DATA:
        Here is the current list of Seller Leads in the database (JSON format):
        ${JSON.stringify(simplifiedLeads)}

        USER INFO:
        Name: ${user?.name || "Partner"}

        INSTRUCTIONS:
        1. Answer the user's question based on the provided lead data if applicable.
        2. If the user asks about a specific property address, look it up in the JSON above.
        3. If the answer isn't in the data, provide general real estate expertise.
        4. Keep responses conversational but grounded in data.
      `;

      // 3. Call Gemini
      const model = genAI.getGenerativeModel({ model: "gemini-1.5-flash" });

      const chat = model.startChat({
        history: [
          {
            role: "user",
            parts: [{ text: systemPrompt }],
          },
          {
            role: "model",
            parts: [
              {
                text: "Understood. I am BD, ready to assist with real estate insights and lead analysis.",
              },
            ],
          },
        ],
      });

      const result = await chat.sendMessage(userQuery);
      const aiText = result.response.text();

      // 4. Add AI Response
      const aiResponse = {
        id: Date.now() + 1,
        text: aiText,
        sender: "ai",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiResponse]);
    } catch (e) {
      console.error("Error fetching AI response: ", e);
      let errorText =
        "I'm having trouble analyzing the market data right now. Please try again.";

      // Specific error handling for different failure scenarios
      if (!process.env.REACT_APP_GEMINI_API_KEY) {
        errorText =
          "API configuration error. Please check your .env.local file.";
      } else if (e.message?.includes("API key")) {
        errorText = "Invalid API key. Please verify your Gemini API key.";
      } else if (e.status === 429) {
        errorText = "Rate limit exceeded. Please wait a moment and try again.";
      } else if (e.message?.includes("network") || !navigator.onLine) {
        errorText = "Connection error. Please check your internet connection.";
      }

      const errorMessage = {
        id: Date.now() + 1,
        text: errorText,
        sender: "ai",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [inputText, isLoading, leads, user, genAI]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !isLoading) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      <style>{animations}</style>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-8 right-8 h-20 w-20 rounded-full flex items-center justify-center cursor-pointer transform hover:scale-110 transition-transform duration-300 z-40 bg-white dark:bg-gray-800 shadow-lg border-2 border-blue-100 dark:border-gray-700"
      >
        <div className="w-16 h-16 relative">
          <Character isFloating={true} />
          <span className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></span>
        </div>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50 ">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col h-[80vh] animate-[modal-pop-in_0.3s_ease-out_forwards] border border-gray-200 dark:border-gray-700 dark:text-gray-200 dark:bg-gray-800">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 rounded-t-2xl bg-gray-50 dark:bg-gray-800 dark:text-gray-200">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full p-1 bg-blue-100 border border-blue-200">
                  <Character />
                </div>
                <div>
                  <h2 className="font-bold text-lg text-gray-800 dark:text-gray-100">
                    BD (AI Assistant)
                  </h2>
                  <p className="text-xs text-gray-500 font-medium dark:text-gray-400">
                    Real Estate Expert ● Online
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </div>

            {/* Chat Body */}
            <div className="flex-1 p-6 space-y-4 overflow-y-auto bg-gray-50/50 dark:bg-gray-900">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex items-start gap-3 ${
                    msg.sender === "user" ? "justify-end" : ""
                  }`}
                >
                  {msg.sender === "ai" && (
                    <div className="h-8 w-8 rounded-full flex-shrink-0 bg-blue-100 border border-blue-200 overflow-hidden">
                      <Character />
                    </div>
                  )}
                  <div
                    className={`p-3 rounded-2xl max-w-[80%] shadow-sm ${
                      msg.sender === "ai"
                        ? "bg-white text-gray-800 border border-gray-100 rounded-tl-none dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                        : "bg-blue-600 text-white rounded-tr-none dark:bg-blue-600 dark:text-white"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">
                      {msg.text}
                    </p>
                  </div>
                </div>
              ))}

              {/* Typing Animation Block */}
              {isLoading && (
                <div className="flex items-start gap-3 ">
                  <div className="h-8 w-8 rounded-full flex-shrink-0 bg-blue-100 border border-blue-200 overflow-hidden">
                    <Character />
                  </div>
                  <div className="p-3 rounded-2xl bg-white border border-gray-100 rounded-tl-none shadow-sm dark:bg-gray-700 dark:border-gray-600">
                    <TypingIndicator />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-b-2xl">
              <div className="relative">
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask BD about your leads..."
                  disabled={isLoading}
                  className="w-full p-4 pr-24 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-100 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 focus:bg-white dark:focus:bg-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 dark:focus:ring-blue-400/10 transition-all outline-none"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={isLoading || !inputText.trim()}
                  className="absolute inset-y-2 right-2 bg-blue-600 text-white font-semibold px-5 rounded-full hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg active:scale-95"
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </span>
                  ) : (
                    <span className="flex items-center">Send</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistantChat;
